// Webrtc plugin.
import * as actionTypes from '../actionTypes'

// Libraries.
import { handleActions } from 'redux-actions'
import { concat, remove } from 'lodash/fp'

const reducers = {}

// Add the new Track to state.
reducers[actionTypes.TRACK_ADDED] = {
  next (state, action) {
    return concat(state, action.payload)
  }
}

/*
  Bad Workaround: Don't remove the Track information from state when it is
    removed. Instead, just mark it as "ended".

  When a track is removed from a Call, the application only gets the trackId. If
    they try to get the track with that ID, it will not exist in state. This means
    the application cannot unrender the track if they rely on the Track state
    (eg. isLocal or media kind).

  A better alternative would be to remove the Track from state when the Call it
    is used for ends. That would allow the "call:tracksRemoved" event access to
    the state before they were removed so it could include that information
    (instead of the app needing to re-get it).
  That does not work, though, because this reducer does not have access to the Call
    state (meaning we can't figure out which Session was used for the Call to figure
    out which Tracks were in that Session...) and the "end call" action itself doesn't
    have the information needed either.

  So leave it in state instead to make sure the application can access it. This
    should be fixed when the Callstack and WebrtcStack work together better instead
    of the WebrtcStack just emitting all events as soon as something changes at the
    lower-level WebRTC.
*/
reducers[actionTypes.TRACK_REMOVED] = {
  next (state, action) {
    return state.map(track => {
      if (action.payload.trackId === track.trackId) {
        return {
          ...track,
          state: 'ended'
        }
      } else {
        return track
      }
    })
  }
}

// Store the selector used for rendering the track.
reducers[actionTypes.RENDER_TRACKS_FINISH] = {
  next (state, action) {
    return state.map(track => {
      if (action.payload.trackIds.includes(track.trackId)) {
        return {
          ...track,
          containers: concat(track.containers, action.payload.selector)
        }
      } else {
        return track
      }
    })
  }
}

// Remove the selector used for rendering the track.
reducers[actionTypes.REMOVE_TRACKS_FINISH] = {
  next (state, action) {
    // TODO: Only remove one container (instead of all that match)?
    const removeContainer = container => container === action.payload.selector
    return state.map(track => {
      if (action.payload.trackIds.includes(track.trackId)) {
        return {
          ...track,
          containers: remove(removeContainer)(track.containers)
        }
      } else {
        return track
      }
    })
  }
}

// TODO: Update this on an action from the track channel?
reducers[actionTypes.MUTE_TRACKS_FINISH] = {
  next (state, action) {
    return state.map(track => {
      if (action.payload.trackIds.includes(track.trackId)) {
        return {
          ...track,
          muted: true
        }
      } else {
        return track
      }
    })
  }
}

reducers[actionTypes.UNMUTE_TRACKS_FINISH] = {
  next (state, action) {
    return state.map(track => {
      if (action.payload.trackIds.includes(track.trackId)) {
        return {
          ...track,
          muted: false
        }
      } else {
        return track
      }
    })
  }
}

reducers[actionTypes.TRACK_SOURCE_MUTED] = {
  next (state, action) {
    return state.map(track => {
      if (action.payload.trackIds.includes(track.trackId)) {
        return {
          ...track,
          sourceMuted: true
        }
      } else {
        return track
      }
    })
  }
}

reducers[actionTypes.TRACK_SOURCE_UNMUTED] = {
  next (state, action) {
    return state.map(track => {
      if (action.payload.trackIds.includes(track.trackId)) {
        return {
          ...track,
          sourceMuted: false
        }
      } else {
        return track
      }
    })
  }
}

/*
 * Combine all of top-tier reducers into a single reducer,
 *      each with a default state of an empty array.
 */
const reducer = handleActions(reducers, [])
export default reducer
